import React from "react";
import FieldCard from "./FieldCard";
import InputField from "./InputField";

function SectionCard({ title, children }) {
  return (
    <div className="rounded-xl border border-gray-700 bg-gray-900 p-4">
      <div className="mb-3 text-sm font-semibold text-white">{title}</div>
      {children}
    </div>
  );
}

function createDefaultRoof(base = {}) {
  return {
    id: crypto.randomUUID(),
    name: "",
    areaM2: "",
    roofType: "tile",
    pitch: "medium",
    complexity: "standard",
    ...base,
  };
}

function normalizeRoofForEditor(roof = {}) {
  return createDefaultRoof({
    ...roof,
    id: roof.id || crypto.randomUUID(),
    roofType: roof.roofType || "tile",
    pitch: roof.pitch || "medium",
    complexity: roof.complexity || "standard",
  });
}

export default function RoofSectionEditor({
  roofs = [],
  setRoofs,
  errors = {},
}) {
  const addRoof = () => {
    setRoofs((prev) => {
      const safePrev = Array.isArray(prev) ? prev : [];
      return [...safePrev, createDefaultRoof()];
    });
  };

  const updateRoof = (index, field, value) => {
    setRoofs((prev) => {
      const safePrev = Array.isArray(prev) ? prev : [];
      return safePrev.map((item, i) =>
        i === index ? normalizeRoofForEditor({ ...item, [field]: value }) : item
      );
    });
  };

  const removeRoof = (index) => {
    setRoofs((prev) => {
      const safePrev = Array.isArray(prev) ? prev : [];
      return safePrev.filter((_, i) => i !== index);
    });
  };

  const normalizedRoofs = Array.isArray(roofs)
    ? roofs.map(normalizeRoofForEditor)
    : [];

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-gray-700 bg-gray-900 p-4 text-sm text-gray-400">
        Define each roof section separately. Example: main roof, extension roof,
        garage roof.
      </div>

      <button
        type="button"
        onClick={addRoof}
        className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
      >
        Add roof section
      </button>

      <div className="space-y-4">
        {normalizedRoofs.map((roof, index) => (
          <SectionCard
            key={roof.id || `roof-${index}`}
            title={roof.name?.trim() || `Roof section ${index + 1}`}
          >
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <FieldCard label="Section name">
                <InputField
                  value={roof.name ?? ""}
                  onChange={(e) => updateRoof(index, "name", e.target.value)}
                  placeholder="e.g. Garage roof"
                />
              </FieldCard>

              <FieldCard
                label="Roof area"
                unit="m²"
                error={errors[`roofs.${index}`]}
              >
                <InputField
                  type="number"
                  value={roof.areaM2 ?? ""}
                  onChange={(e) =>
                    updateRoof(
                      index,
                      "areaM2",
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                  placeholder="e.g. 120"
                />
              </FieldCard>

              <FieldCard label="Roof type">
                <select
                  value={roof.roofType || "tile"}
                  onChange={(e) => updateRoof(index, "roofType", e.target.value)}
                  className="w-full rounded border border-gray-600 bg-gray-800 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="tile">Tile roof</option>
                  <option value="metal">Metal roof</option>
                  <option value="flat">Flat roof</option>
                  <option value="bitumen">Bitumen roof</option>
                  <option value="felt">Felt roof</option>
                  <option value="other">Other</option>
                </select>
              </FieldCard>

              <FieldCard label="Roof pitch">
                <select
                  value={roof.pitch || "medium"}
                  onChange={(e) => updateRoof(index, "pitch", e.target.value)}
                  className="w-full rounded border border-gray-600 bg-gray-800 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="flat">Flat</option>
                  <option value="low">Low pitch</option>
                  <option value="medium">Medium pitch</option>
                  <option value="steep">Steep</option>
                </select>
              </FieldCard>

              <FieldCard label="Complexity">
                <select
                  value={roof.complexity || "standard"}
                  onChange={(e) =>
                    updateRoof(index, "complexity", e.target.value)
                  }
                  className="w-full rounded border border-gray-600 bg-gray-800 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="simple">Simple</option>
                  <option value="standard">Standard</option>
                  <option value="complex">Complex</option>
                </select>
              </FieldCard>
            </div>

            <div className="mt-4">
              <button
                type="button"
                onClick={() => removeRoof(index)}
                className="rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700"
              >
                Remove section
              </button>
            </div>
          </SectionCard>
        ))}

        {normalizedRoofs.length === 0 && (
          <div className="rounded-xl border border-amber-700 bg-amber-950/10 p-4 text-sm text-amber-300">
            {errors.roofs || "No roof sections added yet."}
          </div>
        )}
      </div>
    </div>
  );
}